import { Alert } from '@/components/alert/alert';
import { List } from '@/components/list/list';
import { Loader } from '@/components/loader/loader';
import { AccountSettingsIcon } from '@/features/navbar/navbar.styles';
import { getInitials } from '@/helpers/get-initials';
import { colors } from '@/theme/colors';
import { Button } from '@/ui/button/button';
import { Checkbox } from '@/ui/checkbox/checkbox';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Icon } from '@/ui/icons/icon';
import { Stack } from '@/ui/line/line';
import { notify } from '@/ui/snackbar/notify';
import { SmallText } from '@/ui/typography/widgets';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';
import { PRODUCT } from '@/user/products/product.type';
import { useUserProducts } from '@/user/products/use-user-products';
import { useCurrentUser } from '@/user/use-current-user.query';
import { styled } from 'goober';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '../../../ui/tooltip/tooltip';
import { canCopyProjectToAccount } from './can-copy-project-to-account';
import { useCopyProjectsToAccount } from './use-copy-projects-to-account.mutation';

const LIST_MAX_HEIGHT = '320px';

type Account = {
  id: string;
  name: string;
};

export const CopyToAccount = ({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation('projects', { keyPrefix: 'copyToAccount' });
  const [overrideOrganizationDetails, setOverrideOrganizationDetails] =
    useState<boolean>(false);
  const [selectedAccounts, setSelectedAccounts] = useState<Account[]>([]);
  const { data: userData, isLoading } = useCurrentUser();
  const { hasRequiredProduct } = useUserProducts();
  const { hasRequiredPermission } = useUserPermissions();
  const {
    mutate: doCopyProject,
    isPending: isCopying,
    data: copyProjectResponse,
  } = useCopyProjectsToAccount();
  const userAccounts = userData?.userAccounts ?? [];

  const userAccountsList = useMemo(() => {
    return userAccounts
      .filter(v => userData?.account.id !== v.account.id) // Exclude current account
      .map(v => v.account);
  }, [userAccounts, userData?.account.id]);

  const canOverrideOrganizationDetails = hasRequiredProduct(
    PRODUCT.EXTENDED_COPY_PROJECT_TO_ANOTHER_ACCOUNT,
  );
  const hasOtherAccounts = userAccountsList.length > 0;
  const canSubmitCopy = canCopyProjectToAccount({
    hasCopyProjectProduct: hasRequiredProduct(
      PRODUCT.COPY_PROJECT_TO_ANOTHER_ACCOUNT,
    ),
    hasDuplicateStreamPermission: hasRequiredPermission(
      PERMISSION.DUPLICATE_STREAM,
    ),
    hasOtherAccounts,
  });

  const getAccessDeniedMessage = (): string | null => {
    if (!hasOtherAccounts) {
      return null;
    }
    if (!hasRequiredPermission(PERMISSION.DUPLICATE_STREAM)) {
      return t`permissionRequired`;
    }
    if (!hasRequiredProduct(PRODUCT.COPY_PROJECT_TO_ANOTHER_ACCOUNT)) {
      return t`productRequired`;
    }
    return null;
  };

  const accessDeniedMessage = getAccessDeniedMessage();

  const onCopyHandler = async () => {
    if (selectedAccounts.length === 0 || !canSubmitCopy) {
      return;
    }

    const accountIds = selectedAccounts.map(v => v.id);
    await doCopyProject(
      {
        accountId: accountIds[0],
        overrideOrganizationDetails,
        projectIds: [projectId],
      },
      {
        onSuccess: copyProjectResponse => {
          if (copyProjectResponse && copyProjectResponse.errors.length === 0) {
            notify({ message: t`copySuccess` });
            onClose();
          }
        },
      },
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  const isCopyingSuccessful =
    copyProjectResponse && copyProjectResponse.errors.length < 1;

  return (
    <Stack margin="20px 0px">
      {!isCopying && !isCopyingSuccessful && copyProjectResponse && (
        <Alert type="error" messages={copyProjectResponse.errors} />
      )}
      {accessDeniedMessage && !isLoading && (
        <Alert type="error" messages={[accessDeniedMessage]} />
      )}
      <ListTitle>{t`availableAccounts`}</ListTitle>
      <List<Account>
        maxHeight={LIST_MAX_HEIGHT}
        bordered
        items={userAccountsList}
        renderItem={v => (
          <AccountListItem key={`available-account-${v.id}`} name={v.name} />
        )}
        onSelectItem={setSelectedAccounts}
      />
      {canOverrideOrganizationDetails && (
        <Flexbox
          alignItems="center"
          gap="4px"
          name="override-organization-details"
          padding="16px 7px 0px 7px"
        >
          <Checkbox
            checked={overrideOrganizationDetails}
            id="override-organization-details-checkbox"
            label={t`overrideOrganizationDetails`}
            onChange={e => setOverrideOrganizationDetails(e.target.checked)}
          />
          <Tooltip
            content={t`overrideOrganizationDetailsInfo`}
            maxWidth="400px"
          >
            <Icon color={colors.gray.c8} icon="Alert" />
          </Tooltip>
        </Flexbox>
      )}
      <ActionFooter
        onClose={onClose}
        isLoading={isCopying}
        isPrimaryActionDisabled={selectedAccounts.length > 0 && canSubmitCopy}
        onCopyHandler={onCopyHandler}
      />
    </Stack>
  );
};

const ActionFooter = ({
  onClose,
  isPrimaryActionDisabled,
  isLoading,
  onCopyHandler,
}: {
  onClose?: () => void;
  isPrimaryActionDisabled?: boolean;
  isLoading?: boolean;
  onCopyHandler: () => void;
}) => {
  const { t } = useTranslation('projects', { keyPrefix: 'copyToAccount' });

  return (
    <Stack margin="32px 0px 8px 0px" fullWidth gap="16px">
      <Flexbox name="available-account-warning" gap="4px" alignItems="center">
        <Icon color={colors.gray.c8} icon="Alert" />
        <Tooltip content={t`subWarning`} maxWidth="700px">
          <SmallText>{t`warning`}</SmallText>
        </Tooltip>
      </Flexbox>

      <Flexbox justify="right">
        <Flexbox name="available-account-button" gap="24px" alignItems="center">
          <Button
            variant="underlined"
            color={colors.basics.black}
            onClick={onClose}
          >{t`cancel`}</Button>
          <Button
            startIcon="Send"
            endIcon={isLoading ? 'Loader' : undefined}
            disabled={!isPrimaryActionDisabled}
            isLoading={isLoading}
            onClick={onCopyHandler}
          >{t`copyAction`}</Button>
        </Flexbox>
      </Flexbox>
    </Stack>
  );
};

const AccountListItem = ({ name }: { name: string; projectCount?: number }) => {
  return (
    <Flexbox name={`available-account-${name}`} alignItems="center" gap="8px">
      <AccountSettingsIcon
        label={getInitials(name)}
        selected={true}
        isButtonHovered={false}
      />
      <AccountName>{name}</AccountName>
    </Flexbox>
  );
};

const ListTitle = styled('h4')`
  ${({ theme }) => theme.typography.copyProjectToAccount.listTitle};
  padding: 0px 7px;
`;

const AccountName = styled(SmallText)`
  color: ${({ theme }) => theme.colors.basics.black};
`;
